import React from "react";
import Icon from "@mdi/react";
import {mdiHelpCircleOutline} from "@mdi/js";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {HELP_TOOLTIP_DELAY, fixedPopper} from "./tooltipConfig";
import {TipBody, useStickyTip} from "./TipDetail";

/**
 * Kleines Fragezeichen-Symbol, das beim Hover (oder Fokus) einen erklärenden
 * Tooltip einblendet. Gedacht für Überschriften, Labels oder Funktionen, um
 * Kontext, Regeln oder Verhalten eines Bereichs zu dokumentieren.
 *
 * Bewusst generisch gehalten, damit es überall im Backend wiederverwendet
 * werden kann.
 *
 * @param {React.ReactNode} children - Inhalt des Tooltips (Text oder JSX), möglichst kurz.
 * @param {React.ReactNode} [detail] - Langtext, den „mehr" aufklappt (Ticket #127).
 * @param {string} [placement="right"] - Platzierung des Overlays (Bootstrap).
 * @param {number} [size=0.85] - Icon-Größe in @mdi/react-Einheiten (~rem).
 * @param {string} [id="help-tooltip"] - Optionale ID des Tooltips (a11y).
 * @param {string} [className=""] - Zusätzliche Klassen für das Trigger-Element.
 * @param {string} [iconPath=mdiHelpCircleOutline] - MDI-Pfad für ein gleich gestaltetes Symbol.
 * @param {string} [ariaLabel="Hilfe"] - Zugängliche Bezeichnung des Triggers.
 * @param {Function} [onClick] - Optionale Aktion beim Klick bzw. per Tastatur.
 */
export default function HelpTooltip({
    children,
    detail,
    placement = "right",
    size = 0.85,
    id = "help-tooltip",
    className = "",
    iconPath = mdiHelpCircleOutline,
    ariaLabel = "Hilfe",
    onClick,
}) {
    const sticky = useStickyTip(!!detail);
    const handleKeyDown = (event) => {
        if (!onClick || (event.key !== "Enter" && event.key !== " ")) {
            return;
        }

        event.preventDefault();
        onClick(event);
    };

    return (
        <OverlayTrigger
            placement={placement}
            flip
            delay={HELP_TOOLTIP_DELAY}
            {...(detail ? {show: sticky.show, onToggle: sticky.onToggle} : {})}
            popperConfig={fixedPopper([
                {name: "flip", options: {fallbackPlacements: ["bottom", "top", "right", "left"], padding: 8}},
                {name: "preventOverflow", options: {boundary: "viewport", padding: 8, altAxis: true}},
            ])}
            overlay={
                <Tooltip
                    id={id}
                    {...sticky.tipProps}
                    className={`help-tooltip${detail ? " tip-sticky" : ""}`}
                >
                    <TipBody tip={children} detail={detail} open={sticky.open} onOpen={() => sticky.setOpen(true)}/>
                </Tooltip>
            }
        >
            <span
                role="button"
                tabIndex={0}
                aria-label={ariaLabel}
                className={`help-tooltip-trigger d-inline-flex align-items-center text-muted${className ? " " + className : ""}`}
                style={{cursor: onClick ? "pointer" : "help"}}
                onClick={onClick}
                onKeyDown={handleKeyDown}
            >
                <Icon path={iconPath} size={size}/>
            </span>
        </OverlayTrigger>
    );
}
